import type { Disposer } from '@elara/core/types';

import type { NavigationPort, Route } from './routing-contract.js';

import { effect } from '@preact/signals-react';

import { getAnalyticsReporter } from '#lib/analytics.js';
import { matchPath } from '#lib/route-utils.js';
import { createState } from '#lib/signals-utils.js';

import { ROUTES } from './routing-contract.js';

/**
 * Every canonical route pattern, longest (most segments) first so `/trip/:id/:panel` is preferred
 * over `/trip/:id` when both would match. Used to reduce a concrete path (e.g. `/trip/abc/shortlist`)
 * to its low-cardinality pattern (`/trip/:id/:panel`) for the GA4 `screen_name` dimension.
 */
const ROUTE_PATTERNS: readonly string[] = Object.values(ROUTES).sort(
  (a, b) => b.split('/').length - a.split('/').length,
);

/**
 * Derives a stable, low-cardinality screen name from a concrete route by matching it against the
 * canonical {@link ROUTES} patterns. Falls back to the raw pathname if nothing matches. Never
 * returns resource ids, keeping the GA4 `screen_name` dimension clean.
 */
export function routeToScreenName(route: Route): string {
  for (const pattern of ROUTE_PATTERNS) {
    if (matchPath(route.path, pattern)) {
      return pattern;
    }
  }
  return route.path;
}

/**
 * Auto page/screen-view analytics monitor (shared across web + React Native).
 *
 * Subscribes to the platform {@link NavigationPort} and emits a GA4 screen-view whenever the
 * foreground content changes, via the injected {@link getAnalyticsReporter}. Foreground content is:
 *  - an **entity-preview sheet** (destination/hotel/place/flight guide) when one is open — a lot of
 *    content is presented in sheets that overlay the route rather than changing the pathname, so
 *    these are tracked via `previewTarget` (screen name `preview/<type>`); or
 *  - otherwise the **route** itself (screen name = its low-cardinality pattern).
 *
 * Because it depends only on the shared navigation contract, one implementation serves both platforms
 * (web `RouterState` and the React Navigation shell both implement `NavigationPort`).
 *
 * Web note: rely on this explicit event and leave GA4 Enhanced Measurement's history-based
 * `page_view` off to avoid double-counting SPA navigations.
 */
export function createAnalyticsState(navigationPort: NavigationPort) {
  const state = createState({
    /** Key of the last foreground view logged, so unrelated signal churn doesn't re-fire. */
    _lastKey: undefined as string | undefined,
    /** Active effect disposer (idempotent guard). */
    _dispose: null as Disposer | null,

    startMonitors(): Disposer {
      if (this._dispose) return this._dispose;

      const stop = effect(() => {
        const route = navigationPort.currentRoute;
        const preview = navigationPort.previewTarget;
        if (!route && !preview) {
          return;
        }

        // A preview sheet, when open, is the foreground view; otherwise the underlying route is.
        // The dedupe key includes the preview id so switching between two same-type guides re-fires,
        // while the screen name stays low-cardinality (`preview/destination`, not the id).
        const key = preview ? `preview:${preview.type}:${preview.id}` : `path:${route?.path ?? ''}`;
        if (key === this._lastKey) {
          return;
        }
        this._lastKey = key;

        const screenName = preview ? `preview/${preview.type}` : routeToScreenName(route!);
        getAnalyticsReporter().logScreenView(screenName, { page_path: route?.path });
      });

      this._dispose = () => {
        stop();
        this._dispose = null;
        this._lastKey = undefined;
      };
      return this._dispose;
    },

    stopMonitors(): void {
      this._dispose?.();
    },
  });

  return state;
}

export type AnalyticsState = ReturnType<typeof createAnalyticsState>;
